import React, {useState} from 'react';
import {GetServerSideProps} from 'next';
import {ssrOutletStore, PageOutletStoreComp} from '@graphql/generated/page';
import {AvailableStatus, GetStoreDetailsQuery, useGetStoreDetailsQuery} from '@graphql/generated/graphql';
import {BackBar, Cart, Dot, OutletStoreMenuItem} from '@components/index';
import {useMixOrderStore, usePersistentStore} from 'stores';
import {BANNER_HEIGHT} from 'modules';
import hourFormatter from '@lib/helpers/hour-formatter';

const DAY_ORDERED = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const getServerSideProps: GetServerSideProps = async ctx => {
  const {storeFriendlyPath} = ctx.query;
  const {props} = await ssrOutletStore.getServerPage({variables: {friendlyPath: decodeURI(storeFriendlyPath as string)}}, ctx);
  const {
    data: {store},
  } = props;
  if (!store) {
    return {
      redirect: {
        destination: '/',
        permanent: true,
      },
    };
  }
  return {props};
};

const Store: PageOutletStoreComp = ({
  data: {
    store: {
      friendlyPath,
      outlet: {friendlyPath: outletFriendlyPath},
      id,
      name,
      country,
      banner,
      active,
      menuSection,
    },
  },
}) => {
  const preorderDate = usePersistentStore(s => s.preorderDate);
  const mixOrderInput = useMixOrderStore(s => s.getMixOrderInput(outletFriendlyPath));
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const {data} = useGetStoreDetailsQuery({
    variables: {friendlyPath: friendlyPath},
    fetchPolicy: 'cache-and-network',
  });

  if (!menuSection || !data) return null;

  const {
    store: {
      description,
      categories,
      ongoingPromos,
      menuSection: {openingHours},
      averageRating,
      ratingsCount,
    },
  } = data;

  const {menuCategories} = menuSection;
  const store = mixOrderInput?.stores?.find(({storeId}) => storeId === id);
  const backUrl = preorderDate ? `/outlets/${outletFriendlyPath}/preorder` : `/outlets/${outletFriendlyPath}`;
  const isActive = preorderDate ? true : active;
  const hasCart = !!mixOrderInput?.stores?.length;

  return (
    <section className="pb-12">
      <BackBar isTransparent={true} noShadow={true} backUrl={backUrl} />
      <div className={`flex justify-center items-center overflow-hidden ${BANNER_HEIGHT}`}>
        <img src={banner ?? '/image-placeholder.jpg'} alt={name} className={`bg-white object-cover h-full ${banner ? '' : 'w-full'}`} />
      </div>
      <Header data={data} active={isActive} outletName={name} handleShowDetailsModal={setShowDetailsModal} />
      <div
        className={`fixed max-w-x mx-auto z-90 inset-0 bg-black transform transition ${showDetailsModal ? 'opacity-50' : 'opacity-0 hidden'}`}></div>
      <section
        className={`fixed overflow-scroll inset-0 max-w-md mx-auto px-2 transform transition z-100 ${
          showDetailsModal ? 'translate-y-0' : 'translate-y-full'
        }`}>
        <div
          className={`mt-10 inset-0 flex justify-center items-center transform transition ${
            showDetailsModal ? 'translate-y-0' : 'translate-y-full'
          }`}>
          <div className="bg-white relative shadow rounded-xl justify-center items-center w-full py-5">
            <div className="flex flex-row justify-evenly items-start mb-3">
              <p className="poppins-semibold mb-2 w-2/3 font-lg text-center">{name}</p>
              <div className="absolute right-5 cursor-pointer" onClick={() => setShowDetailsModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 20 20">
                  <path
                    fill="#b7b7b7"
                    d="M10 1.6a8.4 8.4 0 1 0 0 16.8a8.4 8.4 0 0 0 0-16.8zm4.789 11.461L13.06 14.79L10 11.729l-3.061 3.06L5.21 13.06L8.272 10L5.211 6.939L6.94 5.211L10 8.271l3.061-3.061l1.729 1.729L11.728 10l3.061 3.061z"
                  />
                </svg>
              </div>
            </div>

            <div className="paddingHorizontal">
              {!!categories && (
                <div className="flex flex-row justify-center items-center mb-3">
                  {categories.map(({id, name}, index) => {
                    const isLast = index === categories.length - 1;
                    return (
                      <div key={id} className="flex flex-row items-center">
                        <p className="subTextGrayRegular">{name}</p>
                        {!isLast && <Dot color="bg-gray" />}
                      </div>
                    );
                  })}
                </div>
              )}
              {!!averageRating && (
                <div className="flex flex-row justify-center items-center mb-3">
                  <p className="self-center poppins-medium">
                    {averageRating.toFixed(1)}/5 ({ratingsCount})
                  </p>
                  <img src="/icons/star-filled.svg" className="w-5 h-5 mx-1 mb-0.5" />
                </div>
              )}

              {!!ongoingPromos && (
                <div className="flex flex-row justify-center items-center mb-3 flex-wrap">
                  {ongoingPromos.map(({id, name}) => (
                    <div key={id} className="bg-primary rounded-full mr-1 mt-1 px-3 py-0.5">
                      <p className="text-white poppins-medium font-sm">{name}</p>
                    </div>
                  ))}
                </div>
              )}
              {!!description && (
                <div className="grayBottomBorder pt-3 pb-6">
                  <p className="poppins-semibold font-lg mb-1">Info</p>
                  <p className="poppins-medium font-sm">{description}</p>
                </div>
              )}
              <div className="pt-6 pb-3 grayBottomBorder">
                <p className="poppins-semibold font-lg mb-2">Opening Hours</p>
                {DAY_ORDERED.map(dayOrdered => {
                  const currentDayHours = openingHours.filter(({day}) => day === dayOrdered);
                  return (
                    <div key={dayOrdered} className="flex flex-row justify-between items-center mb-3">
                      <p className="poppins-regular font-sm">{dayOrdered}</p>
                      {currentDayHours.length ? (
                        <div className="flex flex-row justify-center items-center">
                          {currentDayHours.map(({open, close}) => (
                            <p key={`${dayOrdered}-${open}-${close}`} className="subTextGrayRegular">
                              {`${hourFormatter(open)} - ${hourFormatter(close)}`}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="poppins-regular font-sm text-error">Closed</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {menuCategories.map(({id, name, availableStatus, menuItems}) => {
        const storeAndCategoryActive = isActive && availableStatus === AvailableStatus.Available;
        return (
          <div key={id} className="paddingHorizontal pt-7">
            <h2 className="poppins-bold font-lg tracking-wide">{name}</h2>
            <div className="flex flex-col">
              {menuItems.map(menuItem => {
                const items = store?.menuItems?.filter(({menuItemId}) => menuItemId === menuItem.id) || [];
                const itemCount = items.reduce((acc, {count}) => acc + count, 0);
                return (
                  <OutletStoreMenuItem
                    key={menuItem.id}
                    country={country}
                    storeAndCategoryActive={storeAndCategoryActive}
                    itemCount={itemCount}
                    item={menuItem}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {hasCart && <Cart.Bar />}
    </section>
  );
};

export default Store;

interface IHeaderProps {
  data: GetStoreDetailsQuery;
  active: boolean;
  outletName: string;
  handleShowDetailsModal: (value: boolean) => void;
}

const Header = ({
  data: {
    store: {averageRating, ratingsCount},
  },
  active,
  outletName: name,
  handleShowDetailsModal,
}: IHeaderProps) => {
  return (
    <div className="sticky top-0 z-10">
      <h1 className="bg-orange-400 py-1 px-5 text-white font-semibold text-center">MENU ITEMS</h1>
      <div className="flex flex-row w-full bg-white paddingHorizontalSmall py-3">
        <div className="flex flex-col flex-1 justify-start items-start">
          <p className="poppins-semibold">{name}</p>
          {!!averageRating && (
            <div className="flex flex-row justify-start items-center mb-1">
              <img src="/icons/star-filled.svg" className="w-4 h-4 mr-1" />
              <p className="self-center poppins-medium font-sm">
                {averageRating.toFixed(1)}/5 ({ratingsCount})
              </p>
            </div>
          )}
          <div className="flex items-center justify-between w-full">
            <button className="flex flex-row justify-start items-center cursor-pointer" onClick={() => handleShowDetailsModal(true)}>
              <p className="text-primary poppins-medium font-sm">Store Details</p>
              {CHEVRON_RIGHT_SVG}
            </button>
            {!active && <p className="text-red-500 font-semibold text-sm">Closed</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const CHEVRON_RIGHT_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#FF9906" className="h-3.5 w-3.5 ml-1.5">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);
